import { observer } from 'mobx-react-lite'
import { PersonalProfileEditorDialog } from '@/features/tax-profile/individual-profiles/components/PersonalProfileEditorDialog'
import { SpousalProfileEditorDialog } from '@/features/tax-profile/individual-profiles/components/SpousalProfileEditorDialog'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { match } from '@/utils/pattern-match-util'
import { DependentProfilesDialogContainer } from '@/features/jobs/details/tax-type/info-gathering/dependents-info/components/DependentProfilesDialog/DependentProfilesDialogContainer'
import { TaxRefundMethodsDialogContainer } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/components/TaxRefundMethodsDialog/TaxRefundMethodsDialogContainer'
import { BusinessProfilesDialogContainer } from '@/features/jobs/details/tax-type/info-gathering/business-info/components/BusinessProfilesDialog/BusinessProfilesDialogContainer'
import { RequestedDocumentDialogContainer } from '@/features/jobs/details/tax-type/info-gathering/documents/components/RequestedDocumentDialog/RequestedDocumentDialogContainer'
import { NotesDialog } from '@/features/jobs/details/tax-type/info-gathering/notes/components/NotesDialog'

export interface TaxInfoGatheringDialogsContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Container component for tax info gathering dialogs.
 */
export const TaxInfoGatheringDialogsContainer = observer(
  function TaxInfoGatheringDialogsContainer({
    vm,
  }: TaxInfoGatheringDialogsContainerProps) {
    const isCompleteInfoGatheringPending =
      vm.infoGatheringVm.confirmCompleteInfoGathering.pending

    return (
      <>
        {vm.infoGatheringVm.allSupportingItemVms.map((itemVm) =>
          match(itemVm, 'type', {
            PersonalInfo: (personal) => (
              <PersonalProfileEditorDialog
                key="PersonalInfo"
                vm={personal.personalProfileEditorVm}
                isDisabled={isCompleteInfoGatheringPending}
              />
            ),
            SpousalInfo: (spousal) => (
              <SpousalProfileEditorDialog
                key="SpousalInfo"
                vm={spousal.spousalProfileEditorVm}
                isDisabled={isCompleteInfoGatheringPending}
              />
            ),
            DependentsInfo: (dependents) => (
              <DependentProfilesDialogContainer
                key="DependentsInfo"
                vm={dependents}
                isDisabled={isCompleteInfoGatheringPending}
              />
            ),
            PersonalTaxRefundMethod: (refund) => (
              <TaxRefundMethodsDialogContainer
                key="PersonalTaxRefundMethod"
                vm={refund}
                isDisabled={isCompleteInfoGatheringPending}
              />
            ),
            BusinessInfo: (businessInfo) => (
              <BusinessProfilesDialogContainer
                key="BusinessInfo"
                vm={businessInfo}
                isDisabled={isCompleteInfoGatheringPending}
              />
            ),
            BusinessTaxRefundMethod: (refund) => (
              <TaxRefundMethodsDialogContainer
                key="BusinessTaxRefundMethod"
                vm={refund}
                isDisabled={isCompleteInfoGatheringPending}
              />
            ),
            Document: (document) => (
              <RequestedDocumentDialogContainer
                key={document.item.id}
                vm={document}
                isDisabled={isCompleteInfoGatheringPending}
              />
            ),
            Notes: (notes) => (
              <NotesDialog
                key={notes.item.id}
                vm={notes}
                isDisabled={isCompleteInfoGatheringPending}
              />
            ),
            [match.DEFAULT]: () => null,
          }),
        )}
      </>
    )
  },
)
